import { Analytics } from '@cobliteam/analytics';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { changeUserFleetRequest } from '@cobliteam/store/src/Login';
import Divider from '@cobliteam/ui/components/Divider';
import DropdownMenu from '@cobliteam/ui/components/DropdownMenu';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@cobliteam/ui-business/components/AppSidebar/components/Sidebar';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from './AppFleetAvatar';

interface Fleet {
  name: string;
  id: string;
}

interface FleetSwitcherProps {
  fleets: Fleet[];
  currentFleet: Fleet;
}

function AppFleetSwitcher({ fleets = [], currentFleet = { name: '', id: '' } }: FleetSwitcherProps) {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const { runtimeFlags } = useDashboard();
  const { openMobile } = useSidebar();

  const handleChangeFleet = (fleetId: string, fleetName: string) => {
    dispatch(changeUserFleetRequest({ id: fleetId, name: fleetName }));
    Analytics.track('FleetSwitcher - Switch - Fleet', { fleetId, fleetName });
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      Analytics.track('FleetSwitcher - Open');
    }
    setOpen(isOpen);
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={open} onOpenChange={handleOpenChange}>
          <DropdownMenu.Trigger asChild>
            {openMobile ? (
              <SidebarMenuButton data-testid='fleet-switcher' size='lg' className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'>
                <div className='flex aspect-square items-center justify-center rounded-lg text-sidebar-primary-foreground'>
                  <Avatar className='size-10 rounded-md'>
                    <AvatarImage src={`https://assets.cobli.co/fleets/${currentFleet.id}/logo.png`} />
                    <AvatarFallback>
                      <img src={`https://ui-avatars.com/api/?name=${currentFleet.name}&background=0234E1&color=fff`} alt={currentFleet.name} className='size-full' />
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div className='grid flex-1 text-left text-sm leading-tight'>
                  <span className='truncate font-semibold'>{currentFleet.name}</span>
                </div>
                <Icon name='chevrons-up-down' className='ml-auto' />
              </SidebarMenuButton>
            ) : (
              <div className='mt-1 cursor-pointer' data-testid='fleet-switcher'>
                <div className='flex items-center justify-center text-sidebar-primary-foreground'>
                  <Tooltip content={currentFleet.name} align='center' position='right' delayDuration={400}>
                    <Avatar className='size-9 rounded-lg shadow-sm'>
                      <AvatarImage src={`https://assets.cobli.co/fleets/${currentFleet.id}/logo.png`} className='rounded-lg border border-gray-6' />
                      <AvatarFallback>
                        <img src={`https://ui-avatars.com/api/?name=${currentFleet.name}&background=0234E1&color=fff`} alt={currentFleet.name} className='size-full' />
                      </AvatarFallback>
                    </Avatar>
                  </Tooltip>
                </div>
              </div>
            )}
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            className={cn('-mt-3 w-80 rounded-lg border-gray-6 px-0 pt-2 pb-2 text-sidebar-foreground')}
            alignOffset={10}
            align='start'
            side={openMobile ? 'bottom' : 'right'}
            collisionPadding={{ left: 10 }}
          >
            <section className='flex flex-col pt-2 pr-2 pb-2 pl-4 font-medium text-gray-12 text-sm'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <span>Lista de Frotas</span>
                </div>
                {!runtimeFlags.noValidGroupOrVehicles && (
                  <Tooltip content='Configurações' portal={false} asChild>
                    <Link to='/config/account' className='cursor-pointer' onClick={() => handleOpenChange(false)}>
                      <Icon name='settings' className='mr-1 size-5 text-gray-10' />
                    </Link>
                  </Tooltip>
                )}
              </div>
            </section>
            <Divider className='mb-4' />
            <SidebarMenu className='px-0'>
              <div
                className={cn(
                  'flex max-h-[calc(100vh-320px)] flex-col gap-2 overflow-y-auto',
                  'scrollbar',
                  'scrollbar-thumb-custom',
                  'scrollbar-thin',
                  'scrollbar-thumb-rounded-full scrollbar-track-rounded-full',
                  'scrollbar-thumb-gray-8',
                  'scrollbar-thumb-bg-clip-content',
                  'scrollbar-track-gray-1'
                )}
              >
                {fleets
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((fleet) => {
                    const isActive = currentFleet.id === fleet.id;
                    const fleetLogo = `https://assets.cobli.co/fleets/${fleet.id}/logo.png`;
                    const fleetAvatarFallback = `https://ui-avatars.com/api/?name=${fleet.name}&background=0234E1&color=fff`;

                    return (
                      <SidebarMenuItem key={fleet.name} className='px-2'>
                        <SidebarMenuButton onClick={() => handleChangeFleet(fleet.id, fleet.name)} className='px-0.5 text-xs capitalize' isActive={isActive}>
                          <Avatar className='size-8 rounded-lg border border-gray-5'>
                            <AvatarImage src={fleetLogo || fleetAvatarFallback} className='rounded-md' />
                            <AvatarFallback className='font-medium text-white'>
                              <img src={fleetAvatarFallback} alt={fleet.name} className='size-full' />
                            </AvatarFallback>
                          </Avatar>
                          <span className='font-medium text-sm'>{fleet.name.toLocaleLowerCase()}</span>
                          {isActive && <Icon name='check' className='ml-auto' />}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
              </div>
            </SidebarMenu>
          </DropdownMenu.Content>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export default AppFleetSwitcher;
